/**
 * Version History Modal
 *
 * View and restore previous versions of a form
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBuilderStore } from '../../stores/builderStore';
import { SkeletonVersionList, Skeleton } from '../ui/Skeleton';

interface VersionHistoryProps {
  formId: string;
  isOpen: boolean;
  onClose: () => void;
}

interface Version {
  id: string;
  versionNumber: number;
  changeSummary: string | null;
  createdBy: { id: string; name: string } | null;
  createdAt: string;
}

interface VersionDetails extends Version {
  schema: object;
  settings: object;
}

export default function VersionHistory({ formId, isOpen, onClose }: VersionHistoryProps) {
  const { t } = useTranslation('builder');
  const [versions, setVersions] = useState<Version[]>([]);
  const [currentVersion, setCurrentVersion] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<Version | null>(null);
  const [versionDetails, setVersionDetails] = useState<VersionDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const { initializeForm, formName } = useBuilderStore();

  useEffect(() => {
    if (isOpen && formId) {
      fetchVersions();
    }
  }, [isOpen, formId]);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/forms/${formId}/versions`);
      if (!response.ok) {
        throw new Error('Failed to fetch versions');
      }
      const data = await response.json() as { versions?: Version[]; currentVersion?: number };
      setVersions(data.versions || []);
      setCurrentVersion(data.currentVersion || 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load versions');
    } finally {
      setLoading(false);
    }
  };

  const fetchVersionDetails = async (version: Version) => {
    try {
      setLoadingDetails(true);
      setSelectedVersion(version);
      const response = await fetch(`/api/forms/${formId}/versions?version=${version.versionNumber}`);
      if (!response.ok) {
        throw new Error('Failed to fetch version details');
      }
      const data = await response.json() as VersionDetails;
      setVersionDetails(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load version details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleRestore = async () => {
    if (!selectedVersion) return;

    try {
      setRestoring(true);
      const response = await fetch(`/api/forms/${formId}/versions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ versionNumber: selectedVersion.versionNumber }),
      });

      if (!response.ok) {
        throw new Error('Failed to restore version');
      }

      const result = await response.json() as { newVersion: number };

      // Reload form data in the builder
      const formResponse = await fetch(`/api/forms/${formId}`);
      if (formResponse.ok) {
        const formData = await formResponse.json() as { schema: object };
        initializeForm(formId, formName, formData.schema as Parameters<typeof initializeForm>[2]);
      }

      alert(t('versions.restoreSuccess', { old: selectedVersion.versionNumber, new: result.newVersion }));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to restore version');
    } finally {
      setRestoring(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTimeDiff = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) return t('versions.minAgo', { count: diffMins });
    if (diffHours < 24) return t('versions.hoursAgo', { count: diffHours });
    if (diffDays < 30) return t('versions.daysAgo', { count: diffDays });
    return formatDate(dateString);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('versions.title')}</h2>
            <p className="text-sm text-gray-500">
              {t('versions.currentVersion', { version: currentVersion })} | {t('versions.savedVersions', { count: versions.length })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Version List */}
          <div className="w-1/3 border-r overflow-y-auto">
            {loading ? (
              <SkeletonVersionList count={5} />
            ) : error ? (
              <div className="p-6 text-center">
                <p className="text-red-600">{error}</p>
                <button
                  onClick={fetchVersions}
                  className="mt-2 text-primary-600 hover:underline"
                >
                  {t('versions.retry')}
                </button>
              </div>
            ) : versions.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="font-medium">{t('versions.noVersionHistory')}</p>
                <p className="text-sm mt-1">{t('versions.noVersionsDescription')}</p>
              </div>
            ) : (
              <div className="divide-y">
                {versions.map((version) => (
                  <button
                    key={version.id}
                    onClick={() => fetchVersionDetails(version)}
                    className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                      selectedVersion?.id === version.id ? 'bg-primary-50 border-l-4 border-primary-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-gray-900">
                        {t('versions.version', { number: version.versionNumber })}
                      </span>
                      <span className="text-xs text-gray-500">
                        {getTimeDiff(version.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 truncate">
                      {version.changeSummary === 'Auto-saved version'
                        ? t('versions.autoSavedVersion')
                        : (version.changeSummary || t('versions.noDescription'))}
                    </p>
                    {version.createdBy && (
                      <p className="text-xs text-gray-400 mt-1">
                        {t('versions.by', { name: version.createdBy.name })}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Version Details */}
          <div className="flex-1 overflow-y-auto p-6">
            {!selectedVersion ? (
              <div className="h-full flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                  </svg>
                  <p className="font-medium">{t('versions.selectVersion')}</p>
                  <p className="text-sm mt-1">{t('versions.clickToSeeDetails')}</p>
                </div>
              </div>
            ) : loadingDetails ? (
              <div className="space-y-6">
                <div>
                  <Skeleton className="h-7 w-32 mb-2" />
                  <div className="flex items-center gap-4 mb-4">
                    <Skeleton className="h-4 w-36" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-20 w-full rounded-lg" />
                </div>
                <div>
                  <Skeleton className="h-5 w-40 mb-2" />
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Skeleton className="w-6 h-6 rounded" />
                        <Skeleton className="h-4 flex-1" />
                        <Skeleton className="h-5 w-16 rounded" />
                      </div>
                    ))}
                  </div>
                </div>
                <Skeleton className="h-12 w-full rounded-lg" />
              </div>
            ) : versionDetails ? (
              <div>
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {t('versions.version', { number: versionDetails.versionNumber })}
                  </h3>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <span>{formatDate(versionDetails.createdAt)}</span>
                    {versionDetails.createdBy && (
                      <span>{t('versions.by', { name: versionDetails.createdBy.name })}</span>
                    )}
                  </div>
                  {versionDetails.changeSummary && (
                    <p className="text-gray-600 bg-gray-50 rounded-lg p-3">
                      {versionDetails.changeSummary === 'Auto-saved version'
                        ? t('versions.autoSavedVersion')
                        : versionDetails.changeSummary}
                    </p>
                  )}
                </div>

                {/* Schema Preview */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-2">{t('versions.fieldsInVersion')}</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    {((versionDetails.schema as { fields?: Array<{ id: string; type: string; label: string }> }).fields || []).map((field, index) => (
                      <div key={field.id || index} className="flex items-center gap-2 text-sm">
                        <span className="w-6 h-6 bg-white rounded flex items-center justify-center text-gray-500 text-xs font-mono">
                          {index + 1}
                        </span>
                        <span className="text-gray-600">{field.label}</span>
                        <span className="text-xs text-gray-400 bg-gray-200 px-2 py-0.5 rounded">
                          {field.type}
                        </span>
                      </div>
                    ))}
                    {((versionDetails.schema as { fields?: Array<{ id: string; type: string; label: string }> }).fields || []).length === 0 && (
                      <p className="text-gray-500 text-sm">{t('versions.noFieldsInVersion')}</p>
                    )}
                  </div>
                </div>

                {/* Restore Button */}
                <button
                  onClick={handleRestore}
                  disabled={restoring}
                  className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors font-medium"
                >
                  {restoring ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('versions.restoring')}
                    </span>
                  ) : (
                    <>{t('versions.restoreToVersion', { number: versionDetails.versionNumber })}</>
                  )}
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  {t('versions.restoreWarning')}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
